import { useCallback, useEffect, useState } from 'react'
import { Lock, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { ErrorState, Skeleton, EmptyState } from '@/components/States'
import { ApiError } from '@/lib/api'
import {
  fetchHolds,
  createHold,
  releaseHold,
  deleteHold,
  AUTHORITIES,
  authLabel,
  LH_STR as S,
  type LegalHold,
} from './legalHoldsApi'

export function LegalHoldsScreen() {
  const { toast } = useToast()
  const [status, setStatus] = useState('')
  const [rows, setRows] = useState<LegalHold[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  const [userId, setUserId] = useState('')
  const [ref, setRef] = useState('')
  const [reason, setReason] = useState('')
  const [authority, setAuthority] = useState('court')
  const [caseRef, setCaseRef] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchHolds(status, ac.signal)
      .then(setRows)
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [tick, status])

  const create = async () => {
    if (!reason.trim() || (!ref.trim() && !userId.trim())) {
      toast(S.failed, { tone: 'error' })
      return
    }
    setBusy(true)
    try {
      await createHold({
        subject_user_id: userId.trim() ? Number(userId) : undefined,
        subject_ref: ref.trim(),
        reason: reason.trim(),
        authority,
        case_ref: caseRef.trim(),
      })
      setUserId('')
      setRef('')
      setReason('')
      setCaseRef('')
      toast(S.done, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }
  const release = async (id: number) => {
    try {
      await releaseHold(id)
      toast(S.done, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }
  const del = async (id: number) => {
    try {
      await deleteHold(id)
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }

  return (
    <>
      <PageHeader title={S.title} screenId="A19.06" description={S.desc} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-3xl space-y-4">
          {/* Create */}
          <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border-default bg-surface-2 p-3">
            <input
              value={userId}
              onChange={(e) => setUserId(e.target.value.replace(/\D/g, ''))}
              placeholder={S.subjectUser}
              className="w-36 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
            />
            <input
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              placeholder={S.subjectRef}
              className="min-w-[140px] flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
            />
            <select value={authority} onChange={(e) => setAuthority(e.target.value)} className="rounded-md border border-border-default bg-surface-1 px-2 py-2 text-sm text-primary">
              {AUTHORITIES.map((a) => (
                <option key={a.key} value={a.key}>{a.label}</option>
              ))}
            </select>
            <input
              value={caseRef}
              onChange={(e) => setCaseRef(e.target.value)}
              placeholder={S.caseRef}
              className="w-32 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
            />
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={S.reason}
              className="min-w-[140px] flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
            />
            <button type="button" onClick={create} disabled={busy} className="flex items-center gap-1 rounded-md bg-accent px-3 py-2 text-sm text-white disabled:opacity-50">
              <Lock size={14} />
              {S.add}
            </button>
          </div>

          <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-md border border-border-default bg-surface-2 px-2 py-1 text-sm text-secondary">
            <option value="">{S.all}</option>
            <option value="active">active</option>
            <option value="released">released</option>
          </select>

          {err ? (
            <ErrorState code={err.code} onRetry={reload} />
          ) : !rows ? (
            <div className="space-y-1.5">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState title={S.empty} />
          ) : (
            <div className="space-y-2">
              {rows.map((h) => (
                <div key={h.id} className="rounded-lg border border-border-default bg-surface-2 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-primary">{authLabel(h.authority)}</span>
                        {h.status === 'active' ? (
                          <span className="rounded bg-danger/10 px-1.5 py-0.5 text-xs text-danger">● {S.active}</span>
                        ) : (
                          <span className="rounded bg-surface-3 px-1.5 py-0.5 text-xs text-tertiary">{S.released}</span>
                        )}
                        {h.case_ref && <span className="text-xs text-tertiary">{S.case}: {h.case_ref}</span>}
                      </div>
                      <p className="mt-1 text-sm text-secondary">
                        {S.subject}: <span className="text-primary">{h.subject || '—'}</span>
                      </p>
                      {h.reason && <p className="text-xs text-tertiary">{h.reason}</p>}
                      <p className="mt-0.5 text-xs text-tertiary">
                        {new Date(h.created_at).toLocaleDateString()}
                        {h.expires_at && ` · ⏱ ${new Date(h.expires_at).toLocaleDateString()}`}
                        {h.released_at && ` · ✓ ${new Date(h.released_at).toLocaleDateString()}`}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-1.5">
                      {h.status === 'active' && (
                        <button type="button" onClick={() => release(h.id)} className="rounded-md border border-border-default px-2.5 py-1 text-xs text-secondary hover:text-primary">
                          {S.release}
                        </button>
                      )}
                      <button type="button" onClick={() => del(h.id)} className="rounded-md p-1.5 text-tertiary hover:bg-surface-3 hover:text-danger" title={S.delete}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
